import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  type LadderPrivilege,
  PRIVILEGE_TEXT_MAX_LENGTH,
} from "@/types/progression"

/**
 * OKS-52 — Privileges editor nested inside `<RankLadderForm>`.
 *
 * Renders the privilege rows for ONE rank: each row has an `<Input>`
 * + chevron-up / chevron-down (reorder) + trash (remove). Bottom:
 * `Add privilege` button.
 *
 * # Wire shape (matches `BrandSettingsRanksController` strong params
 * + `BrandRanks::LadderUpdater`):
 *
 *   ladder[ranks][<rankIdx>][privileges][<j>][id]
 *   ladder[ranks][<rankIdx>][privileges][<j>][text]
 *   ladder[ranks][<rankIdx>][privileges][<j>][_destroy]
 *
 * `position` is NOT shipped — server derives it from submitted array
 * index (TRD KD-10).
 *
 * # State model
 *
 * Working state lives in `useState`: an ordered list of
 * `WorkingPrivilege` entries. Chevron / Add / Remove mutate this
 * list locally; the hidden form inputs always mirror it so the
 * Inertia `<Form>` parent submits the live local state.
 *
 * Deletion: the trash icon does NOT splice the row out of the array.
 * It marks `_destroy: true` so the row stays at its index (the
 * `_destroy` hidden input goes along to the server). The row is
 * visually hidden from the rendered list to keep the UI clean,
 * but a hidden `<input>` carrying its `id` + `_destroy=1` ships
 * with the submit.
 */

interface WorkingPrivilege {
  /** Existing-row id if persisted; null for new rows. */
  id: number | null
  /** Local-state stable key (NOT shipped to server). */
  key: string
  text: string
  destroyed: boolean
}

let __keyCounter = 0
const nextKey = () => `new-${++__keyCounter}`

export function PrivilegesEditor({
  rankIdx,
  rankName,
  initialPrivileges,
  errors,
  canEdit,
}: {
  rankIdx: number
  rankName: string
  initialPrivileges: LadderPrivilege[]
  errors: Record<string, string | string[]>
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const fieldsetId = useId()

  const [rows, setRows] = useState<WorkingPrivilege[]>(() =>
    initialPrivileges.map((p) => ({
      id: p.id,
      key: `existing-${p.id}`,
      text: p.text,
      destroyed: false,
    })),
  )

  // Visible rows (not yet marked for destruction) — these are the
  // ones the chevron/trash UI acts on, and the only ones whose
  // submitted index is meaningful to the server (server derives
  // `position` from this submitted order).
  const visibleRows = rows.filter((r) => !r.destroyed)
  const destroyedRows = rows.filter((r) => r.destroyed)

  const updateRow = (key: string, patch: Partial<WorkingPrivilege>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  const moveUp = (key: string) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key)
      if (idx <= 0) return prev
      // Find the previous visible row (not destroyed).
      let swapIdx = idx - 1
      while (swapIdx >= 0 && prev[swapIdx].destroyed) swapIdx--
      if (swapIdx < 0) return prev
      const next = [...prev]
      ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
      return next
    })
  }

  const moveDown = (key: string) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key)
      if (idx < 0 || idx >= prev.length - 1) return prev
      let swapIdx = idx + 1
      while (swapIdx < prev.length && prev[swapIdx].destroyed) swapIdx++
      if (swapIdx >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
      return next
    })
  }

  const remove = (key: string) => {
    const row = rows.find((r) => r.key === key)
    if (!row) return

    if (row.id == null) {
      // New (unpersisted) row — splice it out entirely; no server
      // round-trip needed.
      setRows((prev) => prev.filter((r) => r.key !== key))
    } else {
      // Existing row — mark for `_destroy: 1` ship.
      updateRow(key, { destroyed: true })
    }
  }

  const add = () => {
    setRows((prev) => [
      ...prev,
      { id: null, key: nextKey(), text: "", destroyed: false },
    ])
  }

  const fieldNamePrefix = `ladder[ranks][${rankIdx}][privileges]`

  return (
    <fieldset
      data-testid={`rank-${rankIdx}-privileges`}
      className="space-y-3 rounded-md border p-3"
    >
      <legend
        className="text-muted-foreground px-1 text-xs tracking-wide uppercase"
        data-testid={`rank-${rankIdx}-privileges-legend`}
      >
        {t("settings.brand.progression.ranks.privileges_legend", {
          rank: rankName,
        })}
      </legend>

      {/* Visible rows (in submitted order). */}
      {visibleRows.length === 0 ? (
        <p
          data-testid={`rank-${rankIdx}-privileges-empty`}
          className="text-muted-foreground text-sm"
        >
          —
        </p>
      ) : null}

      {visibleRows.map((row, visibleIdx) => {
        // Visible rows ship at their visible-only index; destroyed
        // rows append after (see the `destroyedRows.map` block
        // below). This keeps indexes contiguous and avoids
        // collisions with the destroyed-row tail.
        const submittedIdx = visibleIdx
        const inputId = `${fieldsetId}-${row.key}`
        const errorKey = `ranks[${rankIdx}][privileges][${submittedIdx}][text]`
        const fieldError = errors[errorKey]
        const errorMessages =
          fieldError == null
            ? undefined
            : Array.isArray(fieldError)
              ? fieldError
              : [fieldError]
        const overLimit = row.text.length > PRIVILEGE_TEXT_MAX_LENGTH

        return (
          <div
            key={row.key}
            data-testid={`rank-${rankIdx}-privilege-row-${submittedIdx}`}
            className="flex flex-col gap-2 sm:flex-row sm:items-start"
          >
            {row.id != null ? (
              <input
                type="hidden"
                name={`${fieldNamePrefix}[${submittedIdx}][id]`}
                value={row.id}
              />
            ) : null}
            <div className="flex-1 space-y-1">
              <Label htmlFor={inputId} className="sr-only">
                {t("settings.brand.progression.ranks.privilege_placeholder")}
              </Label>
              <Input
                id={inputId}
                type="text"
                name={`${fieldNamePrefix}[${submittedIdx}][text]`}
                value={row.text}
                onChange={(e) => updateRow(row.key, { text: e.target.value })}
                maxLength={PRIVILEGE_TEXT_MAX_LENGTH + 1}
                placeholder={t(
                  "settings.brand.progression.ranks.privilege_placeholder",
                )}
                readOnly={!canEdit}
                aria-readonly={!canEdit}
                aria-invalid={errorMessages ? true : undefined}
                data-testid={`rank-${rankIdx}-privilege-input-${submittedIdx}`}
              />
              <div className="flex items-center justify-between gap-2">
                <InputError messages={errorMessages} />
                <span
                  className={
                    overLimit
                      ? "text-destructive text-xs"
                      : "text-muted-foreground text-xs"
                  }
                  data-testid={`rank-${rankIdx}-privilege-counter-${submittedIdx}`}
                >
                  {t(
                    "settings.brand.progression.ranks.privilege_char_counter",
                    {
                      current: row.text.length,
                      max: PRIVILEGE_TEXT_MAX_LENGTH,
                    },
                  )}
                </span>
              </div>
            </div>
            {canEdit ? (
              <div className="flex items-center gap-1 sm:pt-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => moveUp(row.key)}
                  aria-label={t(
                    "settings.brand.progression.ranks.move_up_aria",
                  )}
                  data-testid={`rank-${rankIdx}-privilege-up-${submittedIdx}`}
                  disabled={submittedIdx === 0}
                  className="size-11 sm:size-9"
                >
                  <ChevronUp aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => moveDown(row.key)}
                  aria-label={t(
                    "settings.brand.progression.ranks.move_down_aria",
                  )}
                  data-testid={`rank-${rankIdx}-privilege-down-${submittedIdx}`}
                  disabled={submittedIdx === visibleRows.length - 1}
                  className="size-11 sm:size-9"
                >
                  <ChevronDown aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => remove(row.key)}
                  aria-label={t(
                    "settings.brand.progression.ranks.remove_privilege_aria",
                  )}
                  data-testid={`rank-${rankIdx}-privilege-remove-${submittedIdx}`}
                  className="size-11 sm:size-9"
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </div>
            ) : null}
          </div>
        )
      })}

      {/* Hidden inputs for rows pending destruction. They stay at the
          tail of the submitted array so the visible rows' indexes
          (which drive server-side `position`) aren't disturbed. */}
      {destroyedRows.map((row, destroyIdx) => {
        if (row.id == null) return null
        const submittedIdx = visibleRows.length + destroyIdx
        return (
          <div key={row.key}>
            <input
              type="hidden"
              name={`${fieldNamePrefix}[${submittedIdx}][id]`}
              value={row.id}
            />
            <input
              type="hidden"
              name={`${fieldNamePrefix}[${submittedIdx}][_destroy]`}
              value="1"
            />
          </div>
        )
      })}

      {canEdit ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          data-testid={`rank-${rankIdx}-privilege-add`}
        >
          <Plus aria-hidden="true" />
          {t("settings.brand.progression.ranks.add_privilege")}
        </Button>
      ) : null}
    </fieldset>
  )
}
